import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, MapPin, Wine, Leaf, ForkKnife, Info, Star, Thermometer, Droplets, Mountains } from '@phosphor-icons/react';
import { Breadcrumb } from '../Product/Breadcrumb';
import { normalizeGrapeVarietyName } from '../../utils/grapeVarietyUtils';
import { useToast } from '../../context/ToastContext';

// Import grape variety data functions
import { getGrenacheFranceData } from '../../data/grapeVarieties/grenache_france';
import { getSyrahFranceData } from '../../data/grapeVarieties/syrah_france';
import { getMourvedreFranceData } from '../../data/grapeVarieties/mourvedre_france';
import { getCinsaultFranceData } from '../../data/grapeVarieties/cinsault_france';
import { getCarignanFranceData } from '../../data/grapeVarieties/carignan_france';
import { getPetitVerdotFranceData } from '../../data/grapeVarieties/petit-verdot_france';
import { getCabernetSauvignonFranceData } from '../../data/grapeVarieties/cabernet-sauvignon_france';
import { getMerlotFranceData } from '../../data/grapeVarieties/merlot_france';
import { getChardonnayFranceData } from '../../data/grapeVarieties/chardonnay_france';
import { getSauvignonBlancFranceData } from '../../data/grapeVarieties/sauvignon-blanc_france';
import { getPinotNoirFranceData } from '../../data/grapeVarieties/pinot-noir_france';
import { getPoulsardFranceData } from '../../data/grapeVarieties/poulsard_france';
import { getTrousseauFranceData } from '../../data/grapeVarieties/trousseau_france';
import { getSavagninFranceData } from '../../data/grapeVarieties/savagnin_france';
import { getCounoiseFranceData } from '../../data/grapeVarieties/counoise_france';
import { getMuscardinFranceData } from '../../data/grapeVarieties/muscardin_france';
import { getVaccareseFranceData } from '../../data/grapeVarieties/vaccarese_france';
import { getTerretNoirFranceData } from '../../data/grapeVarieties/terret-noir_france';
import { getPicardanFranceData } from '../../data/grapeVarieties/picardan_france';
import { getGrenacheBlancFranceData } from '../../data/grapeVarieties/grenache-blanc_france';
import { getRoussanneFranceData } from '../../data/grapeVarieties/roussanne_france';
import { getClairetteFranceData } from '../../data/grapeVarieties/clairette_france';
import { getBourboulencFranceData } from '../../data/grapeVarieties/bourboulenc_france';
import { getViognierFranceData } from '../../data/grapeVarieties/viognier_france';

// Import all subregion data functions
import { getRiojaAltaData } from '../../data/subregions/rioja-alta';
import { getRiberaAltaData } from '../../data/subregions/ribera-alta';
import { getCimaCorgoData } from '../../data/subregions/cima-corgo';
import { getAlentejoCentralData } from '../../data/subregions/alentejo-central';
import { getSerraDaEstrelaData } from '../../data/subregions/serra-da-estrela';
import { getMoncaoMelgacoData } from '../../data/subregions/moncao-melgaco';
import { getChiantiClassicoData } from '../../data/subregions/chianti-classico';
import { getLangheData } from '../../data/subregions/langhe';
import { getValpolicellaData } from '../../data/subregions/valpolicella';
import { getBolgheriData } from '../../data/subregions/bolgheri';
import { getMaremmaData } from '../../data/subregions/maremma';
import { getArboisData } from '../../data/subregions/arbois';
import { getChateauChalonData } from '../../data/subregions/chateau-chalon';
import { getLEtoileData } from '../../data/subregions/l-etoile';
import { getWairauValleyData } from '../../data/subregions/wairau-valley';
import { getGimblettGravelsData } from '../../data/subregions/gimblett-gravels';
import { getBannockburnData } from '../../data/subregions/bannockburn';
import { getMartinboroughData } from '../../data/subregions/martinborough';
import { getGladstoneData } from '../../data/subregions/gladstone';
import { getMastertonData } from '../../data/subregions/masterton';
import { getWaiparaValleyData } from '../../data/subregions/waipara-valley';
import { getNorthCanterburyData } from '../../data/subregions/north-canterbury';
import { getCentralCanterburyData } from '../../data/subregions/central-canterbury';
import { getMoutereHillsData } from '../../data/subregions/moutere-hills';
import { getWaimeaPlainsData } from '../../data/subregions/waimea-plains';
import { getTasmanBayData } from '../../data/subregions/tasman-bay';
import { getSudsteiermarkData } from '../../data/subregions/sudsteiermark';
import { getWeststeiermarkData } from '../../data/subregions/weststeiermark';
import { getOststeiermarkData } from '../../data/subregions/oststeiermark';
import { getNussbergData } from '../../data/subregions/nussberg';
import { getBisambergData } from '../../data/subregions/bisamberg';
import { getMauerData } from '../../data/subregions/mauer';
import { getCahorsData } from '../../data/subregions/cahors';
import { getBergeracData } from '../../data/subregions/bergerac';
import { getMadiranData } from '../../data/subregions/madiran';
import { getJuranconData } from '../../data/subregions/jurancon';
import { getMedocData } from '../../data/subregions/medoc';
import { getSaintEmilionData } from '../../data/subregions/saint_emilion';
import { getGravesData } from '../../data/subregions/graves';
import { getBandolData } from '../../data/subregions/bandol';
import { getCotesProvenceData } from '../../data/subregions/cotes_provence';
import { getCassisData } from '../../data/subregions/cassis';
import { getLenswoodData } from '../../data/subregions/lenswood';
import { getPenolaData } from '../../data/subregions/penola';
import { getClareData } from '../../data/subregions/clare';
import { getUpperYarraValleyData } from '../../data/subregions/upper-yarra-valley';
import { getBlewittSpringsData } from '../../data/subregions/blewitt-springs';
import { getWilyabrupData } from '../../data/subregions/wilyabrup';
import { getLowerHunterValleyData } from '../../data/subregions/lower-hunter-valley';
import { getHighEdenData } from '../../data/subregions/high-eden';
import { getEdenValleyData } from '../../data/subregions/eden-valley';
import { getChateauneufDuPapeData } from '../../data/subregions/chateauneuf-du-pape';

// Subregion data mapping
const subregionDataMap = {
  'rioja-alta': getRiojaAltaData,
  'ribera-alta': getRiberaAltaData,
  'cima-corgo': getCimaCorgoData,
  'alentejo-central': getAlentejoCentralData,
  'serra-da-estrela': getSerraDaEstrelaData,
  'moncao-melgaco': getMoncaoMelgacoData,
  'chianti-classico': getChiantiClassicoData,
  'langhe': getLangheData,
  'valpolicella': getValpolicellaData,
  'bolgheri': getBolgheriData,
  'maremma': getMaremmaData,
  'arbois': getArboisData,
  'chateau-chalon': getChateauChalonData,
  'l-etoile': getLEtoileData,
  'wairau-valley': getWairauValleyData,
  'gimblett-gravels': getGimblettGravelsData,
  'bannockburn': getBannockburnData,
  'martinborough': getMartinboroughData,
  'gladstone': getGladstoneData,
  'masterton': getMastertonData,
  'waipara-valley': getWaiparaValleyData,
  'north-canterbury': getNorthCanterburyData,
  'central-canterbury': getCentralCanterburyData,
  'moutere-hills': getMoutereHillsData,
  'waimea-plains': getWaimeaPlainsData,
  'tasman-bay': getTasmanBayData,
  'sudsteiermark': getSudsteiermarkData,
  'weststeiermark': getWeststeiermarkData,
  'oststeiermark': getOststeiermarkData,
  'nussberg': getNussbergData,
  'bisamberg': getBisambergData,
  'mauer': getMauerData,
  'cahors': getCahorsData,
  'bergerac': getBergeracData,
  'madiran': getMadiranData,
  'jurancon': getJuranconData,
  'medoc': getMedocData,
  'saint_emilion': getSaintEmilionData,
  'graves': getGravesData,
  'bandol': getBandolData,
  'cotes_provence': getCotesProvenceData,
  'cassis': getCassisData,
  'lenswood': getLenswoodData,
  'penola': getPenolaData,
  'clare': getClareData,
  'upper-yarra-valley': getUpperYarraValleyData,
  'blewitt-springs': getBlewittSpringsData,
  'wilyabrup': getWilyabrupData,
  'lower-hunter-valley': getLowerHunterValleyData,
  'high-eden': getHighEdenData,
  'eden-valley': getEdenValleyData,
  'chateauneuf-du-pape': getChateauneufDuPapeData,
};

// Grape variety data mapping
const grapeVarietyDataMap = {
  'france': {
    'grenache': getGrenacheFranceData,
    'syrah': getSyrahFranceData,
    'mourvedre': getMourvedreFranceData,
    'cinsault': getCinsaultFranceData,
    'carignan': getCarignanFranceData,
    'petit-verdot': getPetitVerdotFranceData,
    'cabernet-sauvignon': getCabernetSauvignonFranceData,
    'merlot': getMerlotFranceData,
    'chardonnay': getChardonnayFranceData,
    'sauvignon-blanc': getSauvignonBlancFranceData,
    'pinot-noir': getPinotNoirFranceData,
    'poulsard': getPoulsardFranceData,
    'trousseau': getTrousseauFranceData,
    'savagnin': getSavagninFranceData,
    'counoise': getCounoiseFranceData,
    'muscardin': getMuscardinFranceData,
    'vaccarese': getVaccareseFranceData,
    'terret-noir': getTerretNoirFranceData,
    'picardan': getPicardanFranceData,
    'grenache-blanc': getGrenacheBlancFranceData,
    'roussanne': getRoussanneFranceData,
    'clairette': getClairetteFranceData,
    'bourboulenc': getBourboulencFranceData,
    'viognier': getViognierFranceData
  }
};

export const GrapeVarietyInfoPage = () => {
  const { country, region, subregion, grapeVariety } = useParams();
  const navigate = useNavigate();
  const [subregionData, setSubregionData] = useState(null);
  const [grapeVarietyData, setGrapeVarietyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Try to load grape variety data first
    const normalizedGrapeVariety = normalizeGrapeVarietyName(grapeVariety);
    const countryVarieties = grapeVarietyDataMap[country];
    const getGrapeVarietyData = countryVarieties?.[normalizedGrapeVariety];
    
    if (getGrapeVarietyData) {
      try {
        if (typeof getGrapeVarietyData !== 'function') {
          console.error('getGrapeVarietyData is not a function:', getGrapeVarietyData);
          setError('Erreur: données du cépage non disponibles.');
          setLoading(false);
          return;
        }
        
        const data = getGrapeVarietyData();
        
        // Check if data has required properties
        if (!data || !data.name) {
          console.error('Grape variety data is missing required properties:', data);
          setError('Données du cépage incomplètes.');
          setLoading(false);
          return;
        }
        
        setGrapeVarietyData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading grape variety data:', error);
        setError('Erreur lors du chargement des données du cépage.');
        setLoading(false);
      }
    } else {
      // Fallback to subregion data if grape variety data not found
      const getSubregionData = subregionDataMap[subregion];
      
      if (getSubregionData) {
        try {
          if (typeof getSubregionData !== 'function') {
            console.error('getSubregionData is not a function:', getSubregionData);
            setError('Erreur: données de l\'appellation non disponibles.');
            setLoading(false);
            return;
          }
          
          const data = getSubregionData();
          
          // Check if data has required properties
          if (!data || !data.name) {
            console.error('Subregion data is missing required properties:', data);
            setError('Données de l\'appellation incomplètes.');
            setLoading(false);
            return;
          }
          
          setSubregionData(data);
          
          // Find the specific grape variety data
          const findGrapeVariety = () => {
            if (Array.isArray(data.grapeVarieties)) {
              return data.grapeVarieties.find(v => 
                normalizeGrapeVarietyName(v.name) === normalizedGrapeVariety
              );
            }
            
            if (typeof data.grapeVarieties === 'object' && !Array.isArray(data.grapeVarieties)) {
              // Check in color-based structure
              for (const [color, varietyList] of Object.entries(data.grapeVarieties)) {
                if (Array.isArray(varietyList)) {
                  const found = varietyList.find(v => 
                    normalizeGrapeVarietyName(v) === normalizedGrapeVariety
                  );
                  if (found) {
                    return {
                      name: found,
                      type: color,
                      percentage: 'Variable',
                      description: `Cépage ${color} cultivé dans ${data.name}`,
                      characteristics: `Caractéristiques typiques des cépages ${color} de cette appellation`,
                      wineStyles: `Styles de vin ${color} caractéristiques de ${data.name}`
                    };
                  }
                }
              }
            }
            
            return null;
          };
          
          const varietyData = findGrapeVariety();
          if (varietyData) {
            setGrapeVarietyData(varietyData);
          } else {
            setError('Cépage non trouvé dans cette appellation');
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error loading subregion data:', error);
          setError('Erreur lors du chargement des données.');
          setLoading(false);
        }
      } else {
        setError('Appellation non trouvée');
        setLoading(false);
      }
    }
  }, [country, subregion, grapeVariety]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }

  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }

  if (!grapeVarietyData) return null;

  // Additional safety check
  if (!grapeVarietyData || typeof grapeVarietyData !== 'object') {
    console.error('grapeVarietyData is invalid:', grapeVarietyData);
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>Données du cépage invalides</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }

  // Check if grapeVarietyData has required properties
  if (!grapeVarietyData.name) {
    console.error('grapeVarietyData is missing name property:', grapeVarietyData);
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>Données du cépage incomplètes - nom manquant</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }

  console.log('Rendering GrapeVarietyInfoPage with data:', {
    grapeVarietyData: !!grapeVarietyData,
    grapeVarietyDataName: grapeVarietyData?.name,
    subregionData: !!subregionData,
    subregionDataName: subregionData?.name,
    region,
    country
  });

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>
            {grapeVarietyData.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>
              {subregionData ? `${subregionData.name} - ` : ''}
              {region} - {country}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '32px' }}>
        {/* Main Content */}
        <div>
          {/* Grape Variety Overview */}
          <section style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#4B164C',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '28px'
              }}>
                🍇
              </div>
              <div>
                <h2 style={{ fontSize: '28px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '700' }}>
                  {grapeVarietyData.name}
                </h2>
                {(grapeVarietyData.percentage || (grapeVarietyData.subregions && grapeVarietyData.subregions[region.toLowerCase()]?.percentage)) && (
                  <span style={{
                    background: '#f0e6f0',
                    color: '#4B164C',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {grapeVarietyData.percentage || grapeVarietyData.subregions[region.toLowerCase()]?.percentage || 'Variable selon l\'appellation'}
                  </span>
                )}
              </div>
            </div>
            
            <p style={{ fontSize: '16px', color: '#333', lineHeight: '1.6', marginBottom: '24px' }}>
              {grapeVarietyData.description}
            </p>

            {/* Terroir-Specific Characteristics */}
            {(grapeVarietyData.characteristics || (grapeVarietyData.subregions && grapeVarietyData.subregions[region.toLowerCase()]?.characteristics)) && subregionData && subregionData.name && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Star size={20} />
                  Caractéristiques spécifiques à {subregionData.name}
                </h3>
                <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.5' }}>
                  {(() => {
                    if (typeof grapeVarietyData.characteristics === 'string') {
                      return grapeVarietyData.characteristics;
                    } else if (typeof grapeVarietyData.characteristics === 'object' && grapeVarietyData.characteristics !== null) {
                      // Handle object format (e.g., {viticulture: "...", climate: "...", soil: "..."})
                      return Object.entries(grapeVarietyData.characteristics)
                        .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`)
                        .join('. ');
                    } else {
                      return grapeVarietyData.subregions[region.toLowerCase()]?.characteristics?.description || 'Caractéristiques spécifiques du cépage dans cette appellation.';
                    }
                  })()}
                </p>
              </div>
            )}

            {/* Wine Styles */}
            {(grapeVarietyData.wineStyles || (grapeVarietyData.subregions && grapeVarietyData.subregions[region.toLowerCase()]?.characteristics)) && (
              <div>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Wine size={20} />
                  Styles de vin
                </h3>
                <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.5' }}>
                  {(() => {
                    if (typeof grapeVarietyData.wineStyles === 'string') {
                      return grapeVarietyData.wineStyles;
                    } else if (typeof grapeVarietyData.wineStyles === 'object' && grapeVarietyData.wineStyles !== null) {
                      // Handle object format (e.g., {red: "...", rose: "..."})
                      return Object.entries(grapeVarietyData.wineStyles)
                        .map(([style, description]) => `${style.charAt(0).toUpperCase() + style.slice(1)}: ${description}`)
                        .join('. ');
                    } else {
                      return 'Les vins de ce cépage sont caractérisés par leur style unique et leur expression du terroir.';
                    }
                  })()}
                </p>
              </div>
            )}

            {/* Regions where this variety is grown */}
            {grapeVarietyData.regions && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={20} />
                  Régions de culture
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {grapeVarietyData.regions.filter(region => region && region.name).map((region, index) => (
                    <div key={index} style={{ background: '#f8f6fa', padding: '16px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                      <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>
                        {region.name}
                      </h4>
                      {region.percentage && (
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                          <strong>Part:</strong> {region.percentage}
                        </div>
                      )}
                      {region.area && (
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                          <strong>Surface:</strong> {region.area}
                        </div>
                      )}
                      {region.description && (
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {region.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Terroir Analysis - Only show if subregionData is available */}
          {subregionData && subregionData.name && (
            <section style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mountains size={24} />
                Analyse du Terroir
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                {subregionData.characteristics?.climate && (
                  <div style={{ background: '#f8f6fa', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Thermometer size={20} color="#4B164C" />
                      <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0', fontWeight: '600' }}>Climat</h4>
                    </div>
                    <p style={{ fontSize: '14px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      {subregionData.characteristics.climate}
                    </p>
                  </div>
                )}
                
                {subregionData.characteristics?.soil && (
                  <div style={{ background: '#f8f6fa', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Mountains size={20} color="#4B164C" />
                      <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0', fontWeight: '600' }}>Sols</h4>
                    </div>
                    <p style={{ fontSize: '14px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      {subregionData.characteristics.soil}
                    </p>
                  </div>
                )}
                
                {subregionData.characteristics?.viticulture && (
                  <div style={{ background: '#f8f6fa', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Leaf size={20} color="#4B164C" />
                      <h4 style={{ fontSize: '16px', color: '#4B164C', margin: '0', fontWeight: '600' }}>Viticulture</h4>
                    </div>
                    <p style={{ fontSize: '14px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      {subregionData.characteristics.viticulture}
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* What Makes This Variety Unique - Only show if subregionData is available */}
          {subregionData && subregionData.name && (
            <section style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
              <h3 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Info size={24} />
                Ce qui rend ce cépage unique à {subregionData.name}
              </h3>
              
              <div style={{ background: '#f0e6f0', padding: '24px', borderRadius: '12px', border: '1px solid #4B164C' }}>
                <p style={{ fontSize: '16px', color: '#4B164C', lineHeight: '1.6', margin: '0 0 16px 0', fontWeight: '500' }}>
                  Le {grapeVarietyData.name} cultivé dans {subregionData.name} se distingue par ses caractéristiques uniques liées au terroir local.
                </p>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div>
                    <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>Expression Terroir</h4>
                    <p style={{ fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      L'influence du climat et des sols de {subregionData.name} donne au {grapeVarietyData.name} des notes minérales et une complexité aromatique particulières.
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>Maturité</h4>
                    <p style={{ fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      Les conditions climatiques locales permettent une maturité optimale, développant des arômes complexes et une structure équilibrée.
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>Style Régional</h4>
                    <p style={{ fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                      Le style de vin produit reflète l'identité viticole de {subregionData.name}, avec des caractéristiques distinctives de la région.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Subregion Info - Only show if subregionData is available */}
          {subregionData && subregionData.name && (
            <section style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
                Appellation {subregionData.name}
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Région</div>
                <div style={{ fontSize: '16px', color: '#4B164C', fontWeight: '600' }}>{region}</div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Pays</div>
                <div style={{ fontSize: '16px', color: '#4B164C', fontWeight: '600' }}>{country}</div>
              </div>
              
              {subregionData.area && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Surface</div>
                  <div style={{ fontSize: '16px', color: '#4B164C', fontWeight: '600' }}>{subregionData.area}</div>
                </div>
              )}
              
              {subregionData.production && (
                <div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Production</div>
                  <div style={{ fontSize: '16px', color: '#4B164C', fontWeight: '600' }}>{subregionData.production}</div>
                </div>
              )}
            </section>
          )}

          {/* Food Pairing - Only show if subregionData is available */}
          {subregionData && subregionData.name && (
            <section style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ForkKnife size={18} />
                Accords Mets-Vins
              </h3>
              
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', margin: '0' }}>
                {subregionData.foodPairing || `Les vins de ${grapeVarietyData.name} de ${subregionData.name} s'accordent parfaitement avec la cuisine locale et les plats traditionnels de la région.`}
              </p>
            </section>
          )}

          {/* Navigation */}
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '24px', border: '1px solid #e8e0e8' }}>
            <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
              Navigation
            </h3>
            
            {subregionData && subregionData.name && (
              <button
                onClick={() => navigate(`/info/countries/${country}/regions/${region}/${subregion}/cepages`)}
                style={{
                  width: '100%',
                  background: '#4B164C',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Tous les cépages de {subregionData.name}
              </button>
            )}
            
            {subregionData && subregionData.name && (
              <button
                onClick={() => navigate(`/info/countries/${country}/regions/${region}/${subregion}`)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                Informations sur {subregionData.name}
              </button>
            )}
            
            {!subregionData && (
              <button
                onClick={() => navigate(`/info/countries/${country}/regions/${region}`)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                Informations sur {region}
              </button>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}; 